<template>
  <div>
  <v-data-table
    :headers="headerList"
    :height="tableBodyHeight"
    :items="itemList"
    :loading="loading"
    class="och-table-override"
    dense
    disable-pagination
    fixed-header
    hide-default-footer
    hide-default-header
    v-if="epicMapping"
    v-resize="updateTableBodyHeightOnResize"
  >
    <template v-slot:top>
      <v-toolbar flat>
        <div class="d-flex align-center">
          <v-btn
            class="mr-2"
            outlined
            fab
            x-small
            @click="$router.push({ name: `export${upperFirst(entityName)}` })"
            color="primary"
          >
            <v-icon>fas fa-arrow-left</v-icon>
          </v-btn>
          {{ title }} - {{ epicMapping.label }}
          <div v-if="filter">
            <v-dialog v-model="dialog" width="500">
              <template v-slot:activator="{ on, attrs }">
                <v-btn
                  :disabled="loading"
                  class="ml-4"
                  color="primary"
                  outlined
                  small
                  v-bind="attrs"
                  v-on="on"
                >
                  Filter Code
                </v-btn>
              </template>

              <v-card>
                <v-card-title> Filter Code </v-card-title>

                <v-card-text>
                  <pre>{{ filter }}</pre>
                </v-card-text>

                <v-card-actions>
                  <v-spacer></v-spacer>
                  <v-btn color="primary" text @click="dialog = false">
                    Close
                  </v-btn>
                </v-card-actions>
              </v-card>
            </v-dialog>
          </div>
        </div>
        <v-spacer></v-spacer>
        <div class="d-flex flex-row align-center">
          <p
            class="caption mb-0 mr-3 text-right och-caption-override"
            v-html="lastDownload"
          ></p>
          <v-form
            method="post"
            :action="`/api/${epicMappingName}/${id}/download`"
          >
            <input :value="epicMapping.label" name="filename" type="hidden" />
            <input
              :key="`item-${index}`"
              :value="JSON.stringify(item)"
              name="itemList[]"
              type="hidden"
              v-for="(item, index) in itemList"
            />
            <div>
              <v-btn
                :disabled="itemList.length < 1"
                color="primary"
                outlined
                small
                type="submit"
                width="100%"
                >Download</v-btn
              >
            </div>
          </v-form>
          <dialog-epic-id-import
            :entity-name="entityName"
            :id="id"
            v-if="entityName && id"
          ></dialog-epic-id-import>
        </div>
      </v-toolbar>
      <v-divider class="mt-1 mb-3"></v-divider>
    </template>
    <template v-slot:header="{ props: { headers } }">
      <thead>
        <tr>
          <th
            class="och-header-override"
            v-for="{ text, itemNumber } in headers"
            :key="itemNumber"
          >
            {{ itemNumber }}<br />
            {{ text }}
          </th>
        </tr>
      </thead>
    </template>
  </v-data-table>
  </div>
</template>

<script>
import { keyBy, upperFirst } from "lodash";
import { mapGetters, mapState } from "vuex";
import axios from "axios";
import moment from "moment";

import conditionalUtil from "@/lib/conditionalUtil";
import DialogEpicIdImport from "./epicIdImport/Dialog";

export default {
  components: {
    DialogEpicIdImport,
  },
  computed: {
    attributeList() {
      const { entityName } = this;

      return this.$store.getters[`attribute/filter`]({ entityName });
    },
    downloadEventList() {
      const eventList = this.filterEventList({
        "data.action": "download",
        "data.entityId": this.id,
        "data.entityType": this.epicMappingName,
      });

      return eventList;
    },
    entityList() {
      return this.$store.state[this.entityName].list;
    },
    entityMap() {
      return keyBy(this.entityList, this.primaryAttributeName);
    },
    epicMapping() {
      const { epicMappingName, id } = this;

      return this.$store.getters[`${epicMappingName}/getById`](id);
    },
    epicMappingName() {
      return `${this.entityName}EpicMapping`;
    },
    lastDownload() {
      const { downloadEventList } = this;

      if (!downloadEventList.length) {
        return "Export has never been downloaded";
      }

      const lastEvent = downloadEventList[downloadEventList.length - 1];

      const timestamp = moment(lastEvent.label).format("M/D/YYYY h:mma ");

      const user = this.userMap[lastEvent.userId];

      if (!user) {
        return timestamp;
      }

      const { email } = user;

      return `Downloaded by ${email}<br>${timestamp}`;
    },
    primaryAttributeName() {
      const primaryAttribute = this.attributeList.find(
        attribute => !!attribute.primaryKey
      );

      if (!primaryAttribute) {
        throw new Error(`No primary attribute for ${this.entityName}`);
      }

      return primaryAttribute.name;
    },
    userMap() {
      return keyBy(this.userList, "userId");
    },
    ...mapGetters({
      filterEventList: "event/filter",
    }),
    ...mapState({
      userList: state => state.user.list,
    }),
  },
  async created() {
    const { entityName, epicMappingName, id } = this;

    this.loading = true;

    const [previewResponse] = await Promise.all([
      axios({
        method: "GET",
        url: `/api/${epicMappingName}/${id}/preview`,
      }),
      this.entityGetList({
        entityName: "attribute",
        optionMap: { reload: false },
      }),
      this.entityGetList({
        entityName,
        optionMap: { reload: false },
      }),
      this.entityGetList({
        entityName: epicMappingName,
        optionMap: { reload: false },
      }),
      this.entityGetList({
        entityName: "event",
        optionMap: { reload: false },
      }),
      this.entityGetList({
        entityName: "user",
        optionMap: { reload: false },
      }),
    ]);

    this.loading = false;

    const {
      data: {
        result: { headerList },
      },
    } = previewResponse;

    let {
      data: {
        result: { itemList },
      },
    } = previewResponse;

    this.headerList = headerList.map(([itemNumber, label]) => ({
      align: "start",
      class: "sticky-header white",
      itemNumber,
      text: label,
      value: itemNumber,
    }));

    const filterList = this.epicMapping.conditionalList;

    if (!Array.isArray(filterList)) {
      this.itemList = itemList;
      return;
    }

    if (!filterList.length) {
      this.itemList = itemList;
      return;
    }

    const [filter] = filterList;

    this.filter = filter;

    itemList = itemList.filter(item => {
      const id = item[this.primaryAttributeName];
      const entity = this.entityMap[id];

      if (typeof entity === "undefined") return false;

      return conditionalUtil.resolveConditional({
        conditional: filter,
        entity,
      });
    });

    this.itemList = itemList;
  },
  data() {
    return {
      dialog: false,
      downloading: false,
      eventPollingId: undefined,
      filter: undefined,
      headerList: [],
      itemList: [],
      loading: false,
      tableBodyHeight: undefined,
    };
  },
  destroyed() {
    const { eventPollingId } = this;

    if (!eventPollingId) {
      return;
    }

    window.clearInterval(eventPollingId);
  },
  methods: {
    async entityGetList({ entityName, optionMap }) {
      return this.$store.dispatch(`${entityName}/list`, optionMap);
    },
    updateTableBodyHeightOnResize() {
      const windowHeight = window.innerHeight;
      const tableHeader = document.querySelector("thead");

      if (!tableHeader) {
        return;
      }

      const tableHeaderBoundingRect = tableHeader.getBoundingClientRect();

      const tableHeaderHeight = windowHeight - tableHeaderBoundingRect.y;

      this.tableBodyHeight = tableHeaderHeight;
    },
    upperFirst,
  },
  mounted() {
    this.updateTableBodyHeightOnResize();
  },
  props: {
    entityName: {
      required: true,
      type: String,
    },
    id: {
      required: true,
      type: Number,
    },
    title: {
      required: true,
      type: String,
    },
  },
};
</script>

<style scoped>
.och-header-override {
  min-width: 100px;
  vertical-align: top;
}
.och-caption-override {
  line-height: 15px !important;
}
</style>
